import { defineStore } from 'pinia';
import { useUserStore } from '@store';

export const useDashboardStore = defineStore('dashboard', {
    state: () => ({
        user_id: null,
        selected_company_id: null,
        start_date: null,
        end_date: null,
        agents: [],
    }),

    actions: {
        async retrieveAgents() {
            if (this.agents.length === 0) {
                const {
                    data: { data: response },
                } = await window.tenantAxios.get('/users/tenant?limit=9999');
                this.agents = response;
                return response;
            }
            return this.agents;
        },

        async retrieveCollectedRevenue(companyId = this.getCompanyId) {
            try {
                const response = await window.tenantAxios.get(`/companies/${companyId}/stats/collected-revenue`);
                return response.data.data;
            } catch (error) {
                return null;
            }
        },

        async retrieveExpectedRevenue(companyId = this.getCompanyId) {
            try {
                const response = await window.tenantAxios.get(`/companies/${companyId}/stats/expected-revenue`);
                return response.data.data;
            } catch (error) {
                return null;
            }
        },

        async retrieveBookedJobs(companyId = this.getCompanyId) {
            try {
                const response = await window.tenantAxios.get(`/companies/${companyId}/stats/jobs`);
                return response.data.data;
            } catch (error) {
                return null;
            }
        },

        async retrieveNewLeads(companyId = this.getCompanyId) {
            try {
                const response = await window.tenantAxios.get(`/companies/${companyId}/stats/new-leads`);
                return response.data.data;
            } catch (error) {
                return null;
            }
        },

        async retrieveLeadsUnanswered(companyId = this.getCompanyId) {
            try {
                const response = await window.tenantAxios.get(`/companies/${companyId}/stats/leads-unanswered`);
                return response.data.data;
            } catch (error) {
                return null;
            }
        },

        async retrievePendingApprovals(companyId = this.getCompanyId) {
            try {
                const response = await window.tenantAxios.get(`/companies/${companyId}/stats/pending-approvals`);
                return response.data.data;
            } catch (error) {
                return null;
            }
        },

        async retrieveSchedulingActions(companyId = this.getCompanyId) {
            try {
                const response = await window.tenantAxios.get(`/companies/${companyId}/stats/scheduling-actions`);
                return response.data.data;
            } catch (error) {
                return null;
            }
        },

        async retrieveGraph(
            days,
            revenue,
            leads,
            companyId = this.getCompanyId,
            user_id = this.getUserId,
            start_date = this.getStartDate,
            end_date = this.getEndDate,
        ) {
            try {
                const response = await window.tenantAxios.get(`/companies/${companyId}/stats/sales-graph`, {
                    params: {
                        days,
                        leads,
                        revenue,
                        user_id,
                        start_date,
                        end_date: end_date ?? start_date,
                    },
                });
                return response.data.data;
            } catch (error) {
                return { revenue: {}, leads: {} };
            }
        },

        async retrieveJobsCount(companyId = this.getCompanyId, status = 'PENDING') {
            try {
                const response = await window.tenantAxios.get(
                    `/companies/${companyId}/stats/jobs-count?status=${status}`,
                );

                return response.data.data;
            } catch (error) {
                return null;
            }
        },

        async retrieveLeadsCount(companyId = this.getCompanyId, status = 'NEW_LEAD') {
            try {
                const response = await window.tenantAxios.get(
                    `/companies/${companyId}/stats/leads-count?status=${status}`,
                );

                return response.data.data;
            } catch (error) {
                return null;
            }
        },
    },

    getters: {
        getUserId(state) {
            return state.user_id;
        },
        getStartDate(state) {
            return state.start_date;
        },
        getEndDate(state) {
            return state.end_date ?? state.start_date;
        },
        getKeys() {
            return [this.getUserId, this.getStartDate, this.getEndDate, this.getTeam];
        },
        createParams: () => {
            return (params) => {
                let query = '';
                let first = true;
                for (const [key, val] of Object.entries(params)) {
                    if (val) {
                        if (first) {
                            query += `?${key}=${val}`;
                            first = false;
                        } else {
                            query += `&${key}=${val}`;
                        }
                    }
                }
                return query;
            };
        },
        isAdmin(state) {
            const userStore = useUserStore();
            return (
                userStore.isAdmin &&
                (state.user_id && state.agents.length ?
                    state.agents.find((agent) => agent.id == state.user_id)?.roles?.includes('ADMIN')
                :   true)
            );
        },
        getAgents(state) {
            return state.agents ?? [];
        },
        getAvatar(state) {
            return state.agents.find((agent) => agent.id == state.user_id)?.avatar ?? null;
        },
        getTeam(state) {
            return state.agents.find((agent) => agent.id == state.user_id)?.team ?? null;
        },
        getSelectAgent(state) {
            return state.agents.find((agent) => agent.id === state.user_id) ?? null;
        },
        getCompanyId: (state) => {
            const userStore = useUserStore();
            return state.selected_company_id ?? userStore.getCompanyId;
        },
    },
});
